/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FiTarget, FiCalendar } from 'react-icons/fi';
import { GoLocation } from 'react-icons/go';
import { MdArrowDropDown } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { isEqual } from 'lodash';

import { flightsSearchParameters, setAirSearchType } from 'action-creators/flights';
/* import { fetchGroup } from 'actions/group'; */
import Checkbox from 'components/Checkbox';
import DateRangePicker from 'components/DateRangePicker';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import BookingSelectLocation from 'components/Selects/BookingSelectLocation';
import BookingSelectTime from 'components/Selects/BookingSelectTime';
import BranchCompanySelect from 'components/Selects/BranchCompanySelect';
import TravelArrangerSelect from 'components/Selects/TravelArrangerSelect';
import AirportSearch from 'containers/AirportsSearch';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { Airport } from 'models/airport';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { FlightStretch } from 'models/flightSearch';
import { GroupedListModel } from 'models/groupedList';
import { MultidestinationTrip } from 'models/multiDestinationTrip';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { AirSearchTypeResponse, getAirSearchType } from 'services/flightService';
import { Guest } from 'services/userGuestService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { dateFormatWeekend } from 'utils/generic';
import { compareDates } from 'utils/time';

import FlightClassSelector from './components/FlightClassSelector';
import FlightTimePicker, { Props as FlightTimePickerProps } from './components/FlightTimePicker';
import { TimeOption } from './components/FlightTimePicker/models';
import {
  Container,
  Fields,
  Field,
  SearchButton,
  TravelKindRowContainer,
  Checkbox as CheckboxContainer,
  BaggageIncluded,
  SearchButtonContainer,
  Sector,
  AddAndRemoveSector,
  AddAndRemoveSectorContainer,
  AirplaneIcon,
} from './styles';
import { RootState } from 'store';

interface Props {
  defaultTravelers?: TravelArrangerUsersAllowed[];
  defaultDeparturePoint?: Airport;
  defaultArrivalPoint?: Airport;
  defaultDepartureDate?: Date;
  defaultDepartureTime?: TimeOption;
  defaultReturnDate?: Date;
  defaultReturnTime?: TimeOption;
  defaultCabin?: string;
  defaultBaggageIncluded: boolean;
  defaultDirectFlight: boolean;
  allowedTravelers: TravelArrangerUsersAllowed[];
  isLoadingAllowedTravelers: boolean;
  isHome?: boolean;
  onClose?: () => void;
  branchCompanies?: GetAllBillingCompanyResponse[];
  isLoadingBranchCompanies: boolean;
  defaultBranchCompany?: GetAllBillingCompanyResponse;
  defaultMultiDestinationTrips?: MultidestinationTrip[];
  defaultFlightStretch?: FlightStretch;
}

export const getBookingSelectValue = (airport: Airport | undefined) => {
  if (!airport) return;

  return {
    name: airport.code,
    detail: `${airport.related_city}, ${airport.country}`,
  };
};

const travelersToGroupedModel = (travelers: TravelArrangerUsersAllowed[]): GroupedListModel[] =>
  travelers.map(
    (t) =>
      ({
        id: t.id,
        name: t.name,
        lastName: t.lastName,
        favorite: t.favorite,
        email: t.email,
        userGroupId: t.userGroupId,
        userGroupName: t.userGroupName,
        entity: t.guest ? 'guest' : 'user',
        disabled:
          t.userGroupId === undefined ||
          t.userGroupId === '' ||
          (t.userGroupId !== undefined &&
            t.userGroupId !== '' &&
            !t.hasDomesticFlightPolicies &&
            !t.hasDomesticHotelPolicies &&
            !t.hasDomesticVehiclePolicies &&
            !t.hasInternationalFlightPolicies &&
            !t.hasInternationalHotelPolicies &&
            !t.hasInternationalVehiclePolicies),
      } as GroupedListModel),
  );

const branchCompanyToGroupedModel = (branchCompany: GetAllBillingCompanyResponse): GroupedListModel => ({
  id: branchCompany.id,
  name: branchCompany.companyName,
});

const FlightsSearch = ({
  defaultTravelers,
  defaultDeparturePoint,
  defaultArrivalPoint,
  defaultDepartureDate,
  defaultDepartureTime,
  defaultReturnDate,
  defaultReturnTime,
  defaultCabin,
  defaultBaggageIncluded,
  defaultDirectFlight,
  allowedTravelers,
  isLoadingAllowedTravelers,
  isHome = true,
  onClose,
  branchCompanies,
  isLoadingBranchCompanies,
  defaultBranchCompany,
  defaultMultiDestinationTrips,
  defaultFlightStretch,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const dispatch = useDispatch();

  const FLIGHT_KIND = [
    { label: t('flightsSearch.type.roundTrip'), value: FlightStretch.ROUND_TRIP, testId: 'flight-search-round-trip' },
    { label: t('flightsSearch.type.oneWay'), value: FlightStretch.ONE_WAY, testId: 'flight-search-one-way' },
    {
      label: t('flightsSearch.type.multiDestination'),
      value: FlightStretch.MULTIDESTINATION,
      testId: 'flight-search-multidestination',
    },
  ];

  const ORIGINAL_CABINS = [
    { text: t('flightsSearch.cabin.economy'), value: 'Economy', valueGroup: 'economy' },
    { text: t('flightsSearch.cabin.premiumEconomy'), value: 'PremiumEconomy', valueGroup: 'economyPremium' },
    { text: t('flightsSearch.cabin.business'), value: 'Business', valueGroup: 'business' },
    { text: t('flightsSearch.cabin.first'), value: 'First', valueGroup: 'firstClass' },
  ];

  const MAX_SELECTED_TRAVELERS = 7;

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [flightStretch, setFlightStretch] = useState<FlightStretch>(defaultFlightStretch);
  const [departurePoint, setDeparturePoint] = useState<Airport | undefined>(defaultDeparturePoint);
  const [arrivalPoint, setArrivalPoint] = useState<Airport | undefined>(defaultArrivalPoint);
  const [departureDate, setDepartureDate] = useState<Date | undefined>(defaultDepartureDate);
  const [departureTime, setDepartureTime] = useState<TimeOption | undefined>(defaultDepartureTime);
  const [cabin, setCabin] = useState<string | undefined>(defaultCabin);
  const [returnDate, setReturnDate] = useState<Date | undefined>(defaultReturnDate);
  const [returnTime, setReturnTime] = useState<TimeOption | undefined>(defaultReturnTime);
  const [travelers, setTravelers] = useState<TravelArrangerUsersAllowed[] | undefined>(defaultTravelers);
  const [baggageIncluded, setBaggageIncluded] = useState<boolean>(defaultBaggageIncluded);
  const [directFlight, setDirectFlight] = useState<boolean>(defaultDirectFlight);
  const [branchCompany, setBranchCompany] = useState<GetAllBillingCompanyResponse | undefined>(defaultBranchCompany);
  const [cabinCodes, setCabinCodes] = useState<any[]>(ORIGINAL_CABINS);
  const [isCabinsLoading, setIsCabinsLoading] = useState(false);
  const [cabinKey, setCabinKey] = useState('cabinKey');
  const groupList = useSelector((state: RootState) => state.travelArrangerGuest.groups);
  const loading = useSelector((state: RootState) => state.travelArrangerGuest.loading);

  
  const [multiDestinationTripSectors, setMultidestinationTripSectors] =
    useState<MultidestinationTrip[]>(defaultMultiDestinationTrips);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  useEffect(() => {
    setTravelers(defaultTravelers);
  }, [defaultTravelers]);

  useEffect(() => {
    setBranchCompany(defaultBranchCompany);
  }, [defaultBranchCompany]);

  useEffect(() => {
    switch (flightStretch) {
      case FlightStretch.ROUND_TRIP: {
        return;
      }
      case FlightStretch.ONE_WAY: {
        setReturnDate(undefined);
        setReturnTime(undefined);
        return;
      }
      case FlightStretch.MULTIDESTINATION: {
        if (multiDestinationTripSectors === undefined || multiDestinationTripSectors.length === 0) {
          setMultidestinationTripSectors([
            {
              sectorNumber: 0,
              arrivalPoint: undefined,
              departureDate: undefined,
              departurePoint: undefined,
              departureTime: undefined,
            },
            {
              sectorNumber: 1,
              arrivalPoint: undefined,
              departureDate: undefined,
              departurePoint: undefined,
              departureTime: undefined,
            },
          ]);
        }
      }
    }
  }, [flightStretch, multiDestinationTripSectors]);

  const setMultidestinationDeparturePoint = (value: Airport, sectorNumber?: number) => {
    const tempTrips = [...multiDestinationTripSectors];
    tempTrips[sectorNumber].departurePoint = value;
    setMultidestinationTripSectors(tempTrips);
  };

  const setMultidestinationArrivalPoint = (value: Airport, sectorNumber?: number) => {
    const tempTrips = [...multiDestinationTripSectors];
    tempTrips[sectorNumber].arrivalPoint = value;
    setMultidestinationTripSectors(tempTrips);
  };

  const setMultidestinationDate = (dates: { initialDate: Date; finalDate: Date }, sectorNumber: number) => {
    const tempTrips = [...multiDestinationTripSectors];
    tempTrips[sectorNumber].departureDate = dates.initialDate;
    setMultidestinationTripSectors(tempTrips);
  };

  const setMultidestinationTime = (time: TimeOption | undefined, sectorNumber: number) => {
    const tempTrips = [...multiDestinationTripSectors];
    tempTrips[sectorNumber].departureTime = time;
    setMultidestinationTripSectors(tempTrips);
  };

  const addSector = (sectorNumber: number) => {
    if (multiDestinationTripSectors.length < 6) {
      const tempTrips = [...multiDestinationTripSectors];
      tempTrips.push({
        sectorNumber: sectorNumber,
        arrivalPoint: undefined,
        departureDate: undefined,
        departurePoint: undefined,
        departureTime: undefined,
      });

      //reorganiza
      for (let index = 0; index < tempTrips.length; index++) {
        const trip = tempTrips[index];
        trip.sectorNumber = index;
      }

      setMultidestinationTripSectors(tempTrips);
    } else {
      //mensaje
    }
  };

  const removeSector = (sectorNumber: number) => {
    if (multiDestinationTripSectors.length > 2) {
      const tempTrips = [...multiDestinationTripSectors];
      tempTrips.splice(sectorNumber, 1);

      //reorganiza
      for (let index = 0; index < tempTrips.length; index++) {
        const trip = tempTrips[index];
        trip.sectorNumber = index;
      }

      setMultidestinationTripSectors(tempTrips);
    } else {
      //mensaje
    }
  };

  const isValid = useMemo(() => {
    const hasTravelers = !user?.isTravelArranger || (user?.isTravelArranger && travelers && travelers.length > 0);

    if (flightStretch === FlightStretch.ROUND_TRIP) {
      return Boolean(
        departurePoint &&
          arrivalPoint &&
          hasTravelers &&
          departureDate &&
          returnDate &&
          cabin !== undefined &&
          cabin !== '' &&
          !isCabinsLoading,
      );
    }

    if (flightStretch === FlightStretch.ONE_WAY) {
      return Boolean(
        departurePoint &&
          arrivalPoint &&
          hasTravelers &&
          departureDate &&
          cabin !== undefined &&
          cabin !== '' &&
          !isCabinsLoading,
      );
    }

    if (flightStretch === FlightStretch.MULTIDESTINATION) {
      let multiDestinationValid = true;
      if (multiDestinationTripSectors) {
        for (let index = 0; index < multiDestinationTripSectors.length; index++) {
          const trip = multiDestinationTripSectors[index];
          multiDestinationValid = Boolean(
            trip.arrivalPoint &&
              trip.departureDate &&
              trip.departurePoint &&
              hasTravelers &&
              cabin !== undefined &&
              cabin !== '' &&
              !isCabinsLoading,
          );

          if (!multiDestinationValid) {
            return false;
          }
        }
      } else {
        return false;
      }

      return true;
    }
  }, [
    user?.isTravelArranger,
    travelers,
    flightStretch,
    departurePoint,
    arrivalPoint,
    departureDate,
    returnDate,
    cabin,
    isCabinsLoading,
    multiDestinationTripSectors,
  ]);

  const isBranchCompanyValid = useMemo(() => {
    // TODO: Billing companies - descomentar para funcionamiento correcto
    return true;

    if (branchCompanies && branchCompanies.length > 0) {
      if (branchCompany === undefined) {
        return false;
      }
    }
    return true;
  }, [branchCompanies, branchCompany]);

  const transformedTravelersList: GroupedListModel[] = useMemo(
    () => travelersToGroupedModel(allowedTravelers),
    [allowedTravelers],
  );
  const transformedSelectedTravelers: GroupedListModel[] | undefined = useMemo(() => {
    if (!travelers) return undefined;
    return travelersToGroupedModel(travelers);
  }, [travelers]);

  const transformedSelectedBranchCompany: GroupedListModel | undefined = useMemo(() => {
    if (!branchCompany) return undefined;
    return branchCompanyToGroupedModel(branchCompany);
  }, [branchCompany]);

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setDepartureDate(dates?.initialDate);
    setReturnDate(dates?.finalDate);
  };

  const handleClickSearchButton = useCallback(async () => {
    if (flightStretch !== FlightStretch.MULTIDESTINATION) {
      if (departurePoint && departureDate && arrivalPoint && cabin) {
        if (
          baggageIncluded === defaultBaggageIncluded &&
          directFlight === defaultDirectFlight &&
          isEqual(departurePoint, defaultDeparturePoint) &&
          isEqual(arrivalPoint, defaultArrivalPoint) &&
          cabin === defaultCabin &&
          // roundTrip === !(defaultDeparturePoint && !defaultReturnDate) && TODO
          compareDates(departureDate, defaultDepartureDate) &&
          compareDates(returnDate, defaultReturnDate) &&
          isEqual(travelers, defaultTravelers) &&
          isEqual(departureTime, defaultDepartureTime) &&
          isEqual(returnTime, defaultReturnTime) &&
          !isHome
        ) {
          if (onClose) onClose();
          return;
        }
      } else {
        history.push('/corporate/flights', {
          baggageIncluded,
          departurePoint,
          departureDate,
          arrivalPoint,
          cabin,
          flightStretch,
          returnDate,
          travelers,
          branchCompany,
        });
      }
    }

    if (travelers === undefined || travelers?.length === 0) {
      const currentUserAsTraveler: TravelArrangerUsersAllowed = {
        id: user.id,
        userId: user.id,
        name: user.name,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        userGroupName: user.userGroupName,
        userGroupId: user.userGroupId,
        favorite: true, // TODO fijar ese campo
        guest: user.isGuestBookAllowed, // TODO fijar ese campo
        birthDate:  user.birthDate ?  user.birthDate.toString() : '',
        country: user.country,
        documentNumber: user.documentNumber,
        gender: user.gender,
        passportNumber: user.passportNumber,
      };

      travelers.push(currentUserAsTraveler);
    }

    dispatch(
      flightsSearchParameters({
        baggageIncluded,
        departurePoint,
        departureDate,
        arrivalPoint,
        cabin,
        returnDate,
        travelers,
        departureTime,
        returnTime,
        directFlight,
        branchCompany,
        multiDestinationTrips: multiDestinationTripSectors,
        flightStretch,
      }),
    );

    if (isHome) history.push('/corporate/flights');
    else history.replace('/corporate/flights');
  }, [
    departurePoint,
    departureDate,
    arrivalPoint,
    cabin,
    history,
    baggageIncluded,
    flightStretch,
    returnDate,
    travelers,
    branchCompany,
    defaultBaggageIncluded,
    directFlight,
    defaultDirectFlight,
    defaultDeparturePoint,
    defaultArrivalPoint,
    defaultCabin,
    defaultDepartureDate,
    defaultReturnDate,
    defaultTravelers,
    departureTime,
    defaultDepartureTime,
    returnTime,
    defaultReturnTime,
    isHome,
    dispatch,
    multiDestinationTripSectors,
    onClose,
    user.id,
    user.name,
    user.lastName,
    user.email,
    user.phoneNumber,
    user.userGroupName,
    user.userGroupId,
    user.isGuestBookAllowed,
    user.birthDate,
    user.country,
    user.documentNumber,
    user.gender,
    user.passportNumber,
  ]);

  const transformedBranchCompanies = (): GroupedListModel[] =>
    branchCompanies ? branchCompanies?.map((b) => ({ id: b.id, name: b.companyName })) : ([] as GroupedListModel[]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const configureCabins = async (userGroupId: string) => {
    setIsCabinsLoading(true);

    let departureCode = '';
    let arrivalCode = '';
    let airSearchType: AirSearchTypeResponse = {
      isDomestic: true,
      isInternational: false
    };

    if (flightStretch !== FlightStretch.MULTIDESTINATION) {
      departureCode = departurePoint?.code ?? '';
      arrivalCode = arrivalPoint?.code ?? '';

      airSearchType = await getAirSearchType(departureCode, arrivalCode);
      dispatch(setAirSearchType(airSearchType));
    }

    if (flightStretch === FlightStretch.MULTIDESTINATION) {
      let allDomestic = true;
      
      for (const element of multiDestinationTripSectors) {
        
        if (element.departurePoint && element.arrivalPoint && allDomestic) {
          
          const searchResult = await getAirSearchType(element.departurePoint.code, element.arrivalPoint.code);
          dispatch(setAirSearchType(searchResult));

          if (searchResult.isInternational) {
            allDomestic = false;
          }

          airSearchType = searchResult;
        }
      }

      airSearchType.isDomestic = allDomestic;
      airSearchType.isInternational = !allDomestic;
    }
    
    const currentUser = await groupList.filter((group) => group.id === userGroupId);
    const userGroup = currentUser.length === 0 ?  groupList[0] : currentUser[0];
    /* const userGroupEx = await fetchGroup(userGroupId); */
    
    if (airSearchType.isDomestic) {
      if(userGroup.domesticCabinTypes.length === 0){
        userGroup.domesticCabinTypes.push('economy')
      }
      const filteredCabins = ORIGINAL_CABINS.filter((c) => userGroup.domesticCabinTypes.includes(c.valueGroup));

      setCabinCodes(filteredCabins);
    }



    if (airSearchType.isInternational) {
      if(userGroup.internationalCabinTypes.length === 0){
        userGroup.internationalCabinTypes.push('economy')
      }
      const filteredCabins = ORIGINAL_CABINS.filter((c) => userGroup.internationalCabinTypes.includes(c.valueGroup));

      setCabinCodes(filteredCabins);
    }
    
    setCabinKey((Math.random() + 1).toString(36).substring(7));
    window.setTimeout(() => setIsCabinsLoading(false), 500);
    
  };

  useEffect(() => {
    if(groupList.length > 0){
      if (cabinKey !== 'cabinKey') {
        if (cabinCodes.find((c) => c.value.toLowerCase() === cabin?.toLowerCase()) === undefined) {
          setCabin(undefined);
        } else {
          setCabin(cabin);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinKey]);

  useEffect(() => {
    if (groupList.length > 0) {
      if (flightStretch !== FlightStretch.MULTIDESTINATION && departurePoint && arrivalPoint) {
        configureCabins(
          user?.isTravelArranger
            ? travelers && travelers.length > 0
              ? travelers[0]?.userGroupId
              : user?.userGroupId
            : user?.userGroupId,
        );
      }

      if (
        flightStretch === FlightStretch.MULTIDESTINATION &&
        multiDestinationTripSectors &&
        multiDestinationTripSectors.length > 1
      ) {
        if (multiDestinationTripSectors.first().departurePoint && multiDestinationTripSectors.first().arrivalPoint) {
          configureCabins(
            user?.isTravelArranger
              ? travelers && travelers.length > 0
                ? travelers[0]?.userGroupId
                : user?.userGroupId
              : user?.userGroupId,
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightStretch, travelers, departurePoint, multiDestinationTripSectors, arrivalPoint]);

  const renderedCabins = useMemo(
    () =>
      cabinCodes.map((option) => ({
        id: option.value,
        name: option.text,
      })),
    [cabinCodes],
  );

  const renderRoundTripSelect = () => (
    <TravelKindRowContainer>
      {FLIGHT_KIND.map((flightKind, index) => (
        <TravelKindRowContainer.CheckboxContainer key={index.toString()}>
          <Checkbox
            circle
            checked={flightKind.value === flightStretch}
            onChange={() => setFlightStretch(flightKind.value)}
            testId={flightKind.testId}
          />
          <TravelKindRowContainer.label>{flightKind.label}</TravelKindRowContainer.label>
        </TravelKindRowContainer.CheckboxContainer>
      ))}
    </TravelKindRowContainer>
  );

  //#################### ONE WAY & ROUND TRIP ####################
  const renderDeparturePointSelect = () => (
    <BookingSelectLocation
      label={t('flightsSearch.departurePoint.label')}
      detail={t('flightsSearch.departurePoint.detail')}
      value={getBookingSelectValue(departurePoint)}
      onChange={(value, isCity?: boolean) => {
        setDeparturePoint(value as Airport);
      }}
      icon={<FiTarget />}
      testId="flight-search-departure-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="DEPARTURE"
          {...selectProps}
          placeHolder={t('flightsSearch.departurePoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderArrivalPointSelect = () => (
    <BookingSelectLocation
      label={t('flightsSearch.arrivalPoint.label')}
      detail={t('flightsSearch.arrivalPoint.detail')}
      value={getBookingSelectValue(arrivalPoint)}
      onChange={(value, isCity?: boolean) => {
        setArrivalPoint(value as Airport);
      }}
      icon={<GoLocation />}
      testId="flight-search-arrival-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="ARRIVAL"
          {...selectProps}
          placeHolder={t('flightsSearch.arrivalPoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderDepartureDateSelect = () => (
    <BookingSelectDate
      label={t('flightsSearch.tripDate.departure.label')}
      value={dateFormatWeekend(departureDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="flight-search-departure-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={flightStretch === FlightStretch.ONE_WAY}
          currentInitialDate={departureDate}
          currentFinalDate={returnDate}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangeDepartureTimeSelect = useCallback(
    (time) => {
      if (time === undefined) {
        setDepartureTime(undefined);
        return;
      }
      if (time.indexOf('AM') > -1 || time.indexOf('PM') > -1) {
        setDepartureTime({ label: time, value: time });
        return;
      }
      setDepartureTime({ label: t(`flightsSearch.tripTime.${time}`), value: time });
    },
    [t],
  );

  const isDepartureEnabled = useMemo(() => departureDate === undefined, [departureDate]);
  const renderDepartureTimeSelect = () => (
    <BookingSelectTime
      label={t('flightsSearch.tripDate.departure.title')}
      placeholder={t('flightsSearch.tripDate.departure.placeholder')}
      value={departureTime?.label}
      disabled={isDepartureEnabled}
      onConfirm={handleChangeDepartureTimeSelect}
      icon={<MdArrowDropDown />}
      testId="flight-search-departure-time"
      title={t('flightsSearch.tripDate.departure.title')}
      button={t('flightsSearch.tripDate.departure.button')}
    >
      {(selectProps: FlightTimePickerProps) => <FlightTimePicker {...selectProps} selected={departureTime?.value} />}
    </BookingSelectTime>
  );

  const renderReturnDateSelect = () => (
    <BookingSelectDate
      disabled={flightStretch === FlightStretch.ONE_WAY}
      label={t('flightsSearch.tripDate.return.label')}
      value={dateFormatWeekend(returnDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="flight-search-return-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={departureDate}
          currentFinalDate={returnDate}
          editingInitialDate={false}
          canEditingInitialDate={true}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangeReturnTimeSelect = useCallback(
    (time) => {
      if (time === undefined) {
        setReturnTime(undefined);
        return;
      }
      if (time.indexOf('AM') > -1 || time.indexOf('PM') > -1) {
        setReturnTime({ label: time, value: time });
        return;
      }
      setReturnTime({ label: t(`flightsSearch.tripTime.${time}`), value: time });
    },
    [t],
  );

  const isReturnEnabled = useMemo(() => returnDate === undefined, [returnDate]);
  const renderReturnTimeSelect = () => (
    <BookingSelectTime
      label={t('flightsSearch.tripDate.return.title')}
      placeholder={t('flightsSearch.tripDate.return.placeholder')}
      value={returnTime?.label}
      disabled={isReturnEnabled}
      onConfirm={handleChangeReturnTimeSelect}
      icon={<MdArrowDropDown />}
      testId="flight-search-return-time"
      title={t('flightsSearch.tripDate.return.title')}
      button={t('flightsSearch.tripDate.return.button')}
    >
      {(selectProps: FlightTimePickerProps) => <FlightTimePicker {...selectProps} selected={returnTime?.value} />}
    </BookingSelectTime>
  );

  //#################### MULTIDESTINATION ####################
  const renderMultiDestinationDeparturePointSelect = (
    sectorNumber: number,
    departurePoint: Airport | undefined,
    setDeparturePoint: (value: Airport, sectorNumber?: number) => void,
  ) => (
    <BookingSelectLocation
      label={t('flightsSearch.departurePoint.label')}
      detail={t('flightsSearch.departurePoint.detail')}
      value={getBookingSelectValue(departurePoint)}
      onChange={(value, isCity?: boolean) => {
        setDeparturePoint(value as Airport, sectorNumber);
      }}
      icon={<FiTarget />}
      testId="flight-search-departure-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="MULTIDESTINATION_DEPARTURE"
          {...selectProps}
          placeHolder={t('flightsSearch.departurePoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderMultidestinationArrivalPointSelect = (
    sectorNumber: number,
    arrivalPoint: Airport | undefined,
    setArrivalPoint: (value: Airport, sectorNumber?: number) => void,
  ) => (
    <BookingSelectLocation
      label={t('flightsSearch.arrivalPoint.label')}
      detail={t('flightsSearch.arrivalPoint.detail')}
      value={getBookingSelectValue(arrivalPoint)}
      onChange={(value, isCity?: boolean) => {
        setArrivalPoint(value as Airport, sectorNumber);
      }}
      icon={<GoLocation />}
      testId="flight-search-arrival-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="MULTIDESTINATION_ARRIVAL"
          {...selectProps}
          placeHolder={t('flightsSearch.arrivalPoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderMultidestinationDepartureDateSelect = (
    sectorNumber: number,
    departureDate: Date | undefined,
    setDate: (value: any, sectorNumber: number) => void,
    previousFlightDate: Date | undefined,
  ) => {
    
    const previousDateMultiDestination = multiDestinationTripSectors[sectorNumber - 1]?.departureDate;

    return <BookingSelectDate
      label={t('flightsSearch.tripDate.departure-multidestination.label')}
      value={dateFormatWeekend(departureDate)}
      onChange={(v) => setDate(v, sectorNumber)}
      icon={<FiCalendar />}
      testId="flight-search-departure-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={flightStretch !== FlightStretch.ROUND_TRIP}
          currentInitialDate={departureDate ?? previousDateMultiDestination}
          currentFinalDate={previousFlightDate}
          editingInitialDate={false}
          canEditingInitialDate={true}
          minDate={previousDateMultiDestination}
        />
      )}
    </BookingSelectDate>
  };

  const handleMultidestinationChangeDepartureTimeSelect = useCallback(
    (time: any, sectorNumber: number, setTime: (value: any, sectorNumber: number) => void) => {
      if (time === undefined) {
        setTime(undefined, sectorNumber);
        return;
      }
      if (time.indexOf('AM') > -1 || time.indexOf('PM') > -1) {
        setTime({ label: time, value: time }, sectorNumber);
        return;
      }
      setTime({ label: t(`flightsSearch.tripTime.${time}`), value: time }, sectorNumber);
    },
    [t],
  );

  const renderMultidestinationDepartureTimeSelect = (
    sectorNumber: number,
    departureDate: Date | undefined,
    departureTime: TimeOption | undefined,
  ) => (
    <BookingSelectTime
      label={t('flightsSearch.tripDate.departure-multidestination.title')}
      placeholder={t('flightsSearch.tripDate.departure.placeholder')}
      value={departureTime?.label}
      disabled={departureDate === undefined}
      onConfirm={(v) => handleMultidestinationChangeDepartureTimeSelect(v, sectorNumber, setMultidestinationTime)}
      icon={<MdArrowDropDown />}
      testId="flight-search-departure-time"
      title={t('flightsSearch.tripDate.departure.title')}
      button={t('flightsSearch.tripDate.departure.button')}
    >
      {(selectProps: FlightTimePickerProps) => <FlightTimePicker {...selectProps} selected={departureTime?.value} />}
    </BookingSelectTime>
  );

  const renderClassSelect = () => (
    <FlightClassSelector
      value={cabin}
      onChange={setCabin}
      options={renderedCabins}
      title={t('flightsSearch.cabin.title')}
      testId="flight-search-class"
      key={cabinKey}
      isLoading={isCabinsLoading}
    />
  );

  const renderOptionsDesktop = () => (
    <>
      <Field columns={4}>
        <Field></Field>
        <Field></Field>
        <CheckboxContainer
          checked={directFlight}
          onChange={() => setDirectFlight(!directFlight)}
          testId="flight-search-direct-flight"
        >
          <BaggageIncluded onClick={() => setDirectFlight(!directFlight)}>
            {t('flightsSearch.directFlight')}
          </BaggageIncluded>
        </CheckboxContainer>

        <CheckboxContainer
          checked={baggageIncluded}
          onChange={() => setBaggageIncluded(!baggageIncluded)}
          testId="flight-search-only-baggage-included"
        >
          <BaggageIncluded onClick={() => setBaggageIncluded(!baggageIncluded)}>
            {t('flightsSearch.baggageIncluded')}
          </BaggageIncluded>
        </CheckboxContainer>
      </Field>
    </>
  );

  const renderOptionsMobile = () => (
    <Field>
      <CheckboxContainer
        checked={directFlight}
        onChange={() => setDirectFlight(!directFlight)}
        testId="flight-search-direct-flight"
      >
        <BaggageIncluded onClick={() => setDirectFlight(!directFlight)}>
          {t('flightsSearch.directFlight')}
        </BaggageIncluded>
      </CheckboxContainer>

      <CheckboxContainer
        checked={baggageIncluded}
        onChange={() => setBaggageIncluded(!baggageIncluded)}
        testId="flight-search-only-baggage-included"
      >
        <BaggageIncluded onClick={() => setBaggageIncluded(!baggageIncluded)}>
          {t('flightsSearch.baggageIncluded')}
        </BaggageIncluded>
      </CheckboxContainer>
    </Field>
  );

  const handleTravelerSelect = (items: GroupedListModel[]) => {
    const travelers = [];
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const tempTraveler = allowedTravelers.find((traveler) => traveler.id === item.id);

      if (tempTraveler !== null) {
        travelers.push(tempTraveler);
      }
    }
    setTravelers(travelers);
  };

  const handleBranchCompanySelect = (item: GroupedListModel) => {
    if (branchCompanies) {
      setBranchCompany(branchCompanies?.find((branchCompany) => branchCompany.id === item.id));
    }

    return;
  };

  const handleCreateGuestTraveler = (guest: Guest) => {
    const traveler = {
      guest: true,
      favorite: false,
      id: guest.id || '',
      userId: guest.userId || '',
      name: guest.name,
      lastName: guest.lastName,
      email: guest.email,
      birthDate: guest.birthDate,
      phoneNumber: guest.phoneNumber,
      country: guest.country,
      documentNumber: guest.documentNumber,
      gender: guest.gender,
      passportNumber: guest.passportNumber,
      userGroupId: guest.userGroupId ?? '',
      userGroupName: guest.userGroupName ?? '',
    } as TravelArrangerUsersAllowed;

    allowedTravelers.push(traveler);
    setTravelers([traveler]);
  };

  const isMobile = useIsMobile();

  const renderTravelersSelect = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return (
      <>
        <TravelArrangerSelect
          title={`${t('travelArrangerSelector.placeholder_plural')}`}
          buttonLabel={t('travelArrangerSelector.buttonLabel')}
          addGuestLabel={t('travelArrangerSelector.addGuest')}
          inviteAllowed={user.isGuestBookAllowed}
          options={transformedTravelersList}
          value={transformedSelectedTravelers}
          onChange={handleTravelerSelect}
          onCreateGuest={handleCreateGuestTraveler}
          isLoading={isLoadingAllowedTravelers}
          testId="flight-search-traveler"
          singleSelect={false}
          maxSelectedItems={MAX_SELECTED_TRAVELERS}
        />
      </>
    );
  };

  const renderBranchCompanySelect = () => {
    if (!user) return;
    if (!branchCompanies) return;
    if (branchCompanies.length <= 0) return;

    // TODO - Comentado para no mostrar, debe cambiar para BillingCompanies para no impactar Branch
    return;

    return (
      <BranchCompanySelect
        title={t('billingCompanySelector.placeholder')}
        buttonLabel={t('billingCompanySelector.buttonLabel')}
        options={transformedBranchCompanies()}
        value={transformedSelectedBranchCompany}
        onChange={handleBranchCompanySelect}
        isLoading={isLoadingBranchCompanies}
        testId="hotel-search-branch-company"
      />
    );
  };

  return (
    <>
      <Container>
        <Fields>
          <Field>{renderRoundTripSelect()}</Field>
          {flightStretch !== FlightStretch.MULTIDESTINATION && (
            <>
              <Field>{renderDeparturePointSelect()}</Field>
              <Field>{renderArrivalPointSelect()}</Field>
              <Field columns={2}>
                {renderDepartureDateSelect()} {renderDepartureTimeSelect()}
              </Field>
              <Field columns={2}>
                {renderReturnDateSelect()} {renderReturnTimeSelect()}
              </Field>
            </>
          )}
          {flightStretch === FlightStretch.MULTIDESTINATION &&
            multiDestinationTripSectors &&
            multiDestinationTripSectors.map((m) => (
              <>
                <>
                  <Sector>
                    <AirplaneIcon src="/corporate/images/booking-checkout-airplane.svg" alt="" />{' '}
                    {t('flightsSearch.sector')} {m.sectorNumber + 1}
                    {multiDestinationTripSectors.length > 2 &&
                      m.sectorNumber === multiDestinationTripSectors.length - 1 && (
                        <AddAndRemoveSector onClick={() => removeSector(m.sectorNumber)}>
                          {t('flightsSearch.removeSector')}
                        </AddAndRemoveSector>
                      )}
                  </Sector>
                </>
                <Field columns={isMobile ? 2 : 4}>
                  {renderMultiDestinationDeparturePointSelect(
                    m.sectorNumber,
                    m.departurePoint,
                    setMultidestinationDeparturePoint,
                  )}
                  {renderMultidestinationArrivalPointSelect(
                    m.sectorNumber,
                    m.arrivalPoint,
                    setMultidestinationArrivalPoint,
                  )}
                  {renderMultidestinationDepartureDateSelect(
                    m.sectorNumber,
                    m.departureDate,
                    setMultidestinationDate,
                    m.sectorNumber > 0
                      ? multiDestinationTripSectors[m.sectorNumber - 1].departureDate
                      : multiDestinationTripSectors[m.sectorNumber].departureDate,
                  )}
                  {renderMultidestinationDepartureTimeSelect(m.sectorNumber, m.departureDate, m.departureTime)}
                </Field>
              </>
            ))}

          {flightStretch === FlightStretch.MULTIDESTINATION && (
            <AddAndRemoveSectorContainer>
              {multiDestinationTripSectors && multiDestinationTripSectors.length < 6 && (
                <AddAndRemoveSector onClick={() => addSector(multiDestinationTripSectors.length)}>
                  {t('flightsSearch.addSector')}
                </AddAndRemoveSector>
              )}
            </AddAndRemoveSectorContainer>
          )}

          <Field>{renderClassSelect()}</Field>
          <Field>{renderTravelersSelect()}</Field>
          <Field>{renderBranchCompanySelect()}</Field>
          {isMobile ? renderOptionsMobile() : renderOptionsDesktop()}
        </Fields>
        <SearchButtonContainer>
          <SearchButton
            disabled={!isValid || isLoadingAllowedTravelers || !isBranchCompanyValid}
            onClick={handleClickSearchButton}
            value={t('flightsSearch.searchButton')}
            testId="flight-search-submit"
          />
        </SearchButtonContainer>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text=""
        subText={t('flightsSearch.searchError')}
        icon={IconTypes.Airplane}
        background={BackgroundTypes.Cloud}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('flightsSearch.backButton')}
      />
    </>
  );
};

export default FlightsSearch;
